export interface RFPQuestion {
  id: string;
  category: string;
  subcategory: string;
  question: string;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  responseType: 'Text' | 'Yes/No' | 'Numeric' | 'List' | 'Document';
  scoringCriteria?: string;
  weight?: number;
  template?: string; // SaaS, Consulting, Infrastructure, Security, etc.
  goodAnswer?: string; // Example of a good response
  badAnswer?: string; // Example of a poor response
  redFlags?: string[]; // Warning signs in responses
}

export const RFP_QUESTIONS: RFPQuestion[] = [
  // ==================== COMPANY & STABILITY ====================
  {
    id: 'comp-001',
    category: 'Company & Stability',
    subcategory: 'Company Information',
    question: 'How long has your company been in business?',
    priority: 'High',
    responseType: 'Numeric',
    scoringCriteria: '5+ years = 10, 3-5 years = 7, 1-3 years = 4, <1 year = 1',
    weight: 5,
    template: 'All',
  },
  {
    id: 'comp-002',
    category: 'Company & Stability',
    subcategory: 'Company Information',
    question: 'What is your current number of employees?',
    priority: 'Medium',
    responseType: 'Numeric',
    scoringCriteria: 'Based on project needs',
    weight: 3,
    template: 'All',
  },
  {
    id: 'comp-003',
    category: 'Company & Stability',
    subcategory: 'Company Information',
    question: 'What is your annual revenue (last 3 years)?',
    priority: 'High',
    responseType: 'Numeric',
    scoringCriteria: 'Growth trajectory + stability',
    weight: 5,
    template: 'All',
  },
  {
    id: 'comp-004',
    category: 'Company & Stability',
    subcategory: 'Financial Stability',
    question: 'Are you venture-funded, bootstrapped, or publicly traded?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'Risk assessment based on funding stability',
    weight: 4,
    template: 'SaaS',
  },
  {
    id: 'comp-005',
    category: 'Company & Stability',
    subcategory: 'Financial Stability',
    question: 'What is your current runway if VC-funded?',
    priority: 'Critical',
    responseType: 'Numeric',
    scoringCriteria: '24+ months = 10, 12-24 months = 7, <12 months = 3',
    weight: 8,
    template: 'SaaS',
  },
  {
    id: 'comp-006',
    category: 'Company & Stability',
    subcategory: 'Market Position',
    question: 'How many customers do you currently serve?',
    priority: 'High',
    responseType: 'Numeric',
    scoringCriteria: 'Scale indicator',
    weight: 5,
    template: 'All',
  },
  {
    id: 'comp-007',
    category: 'Company & Stability',
    subcategory: 'Market Position',
    question: 'What is your customer retention rate?',
    priority: 'Critical',
    responseType: 'Numeric',
    scoringCriteria: '>90% = 10, 80-90% = 7, 70-80% = 4, <70% = 2',
    weight: 8,
    template: 'All',
  },
  {
    id: 'comp-008',
    category: 'Company & Stability',
    subcategory: 'Company Information',
    question: 'Who are your key executives and what are their backgrounds?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Industry experience and track record',
    weight: 4,
    template: 'All',
  },
  {
    id: 'comp-009',
    category: 'Company & Stability',
    subcategory: 'Market Position',
    question: 'What is your market share in your primary segment?',
    priority: 'Medium',
    responseType: 'Numeric',
    scoringCriteria: 'Leader vs challenger vs niche',
    weight: 4,
    template: 'All',
  },
  {
    id: 'comp-010',
    category: 'Company & Stability',
    subcategory: 'Financial Stability',
    question: 'Have you been profitable in the last fiscal year?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Profitability indicates stability',
    weight: 6,
    template: 'All',
  },
  {
    id: 'comp-011',
    category: 'Company & Stability',
    subcategory: 'Company Information',
    question: 'How many offices/locations do you operate from?',
    priority: 'Low',
    responseType: 'Numeric',
    scoringCriteria: 'Geographic presence',
    weight: 2,
    template: 'Consulting',
  },
  {
    id: 'comp-012',
    category: 'Company & Stability',
    subcategory: 'Market Position',
    question: 'What percentage of revenue comes from your top 3 customers?',
    priority: 'High',
    responseType: 'Numeric',
    scoringCriteria: '<30% = 10, 30-50% = 7, >50% = 3 (concentration risk)',
    weight: 6,
    template: 'All',
  },

  // ==================== TECHNICAL CAPABILITIES ====================
  {
    id: 'tech-001',
    category: 'Technical Capabilities',
    subcategory: 'Architecture',
    question: 'Describe your system architecture (monolith, microservices, serverless, etc.)',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Matches our scalability needs',
    weight: 9,
    template: 'SaaS',
  },
  {
    id: 'tech-002',
    category: 'Technical Capabilities',
    subcategory: 'Architecture',
    question: 'What is your technology stack (languages, frameworks, databases)?',
    priority: 'Critical',
    responseType: 'List',
    scoringCriteria: 'Modern stack + team expertise',
    weight: 8,
    template: 'SaaS',
  },
  {
    id: 'tech-003',
    category: 'Technical Capabilities',
    subcategory: 'Scalability',
    question: 'What is your current system capacity (concurrent users, transactions/sec)?',
    priority: 'High',
    responseType: 'Numeric',
    scoringCriteria: 'Exceeds our peak load by 3x',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'tech-004',
    category: 'Technical Capabilities',
    subcategory: 'Scalability',
    question: 'How do you handle horizontal scaling?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Auto-scaling + load balancing',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'tech-005',
    category: 'Technical Capabilities',
    subcategory: 'Performance',
    question: 'What are your average API response times (p50, p95, p99)?',
    priority: 'Critical',
    responseType: 'Numeric',
    scoringCriteria: 'p95 <200ms = 10, <500ms = 7, <1s = 4',
    weight: 8,
    template: 'SaaS',
  },
  {
    id: 'tech-006',
    category: 'Technical Capabilities',
    subcategory: 'Performance',
    question: 'What is your historical uptime (last 12 months)?',
    priority: 'Critical',
    responseType: 'Numeric',
    scoringCriteria: '99.99% = 10, 99.9% = 8, 99% = 5, <99% = 2',
    weight: 10,
    template: 'SaaS',
  },
  {
    id: 'tech-007',
    category: 'Technical Capabilities',
    subcategory: 'Integration',
    question: 'What APIs do you provide (REST, GraphQL, webhooks)?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'REST + webhooks minimum',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'tech-008',
    category: 'Technical Capabilities',
    subcategory: 'Integration',
    question: 'Do you provide SDKs? If so, for which languages?',
    priority: 'Medium',
    responseType: 'List',
    scoringCriteria: 'Includes our tech stack',
    weight: 4,
    template: 'SaaS',
  },
  {
    id: 'tech-009',
    category: 'Technical Capabilities',
    subcategory: 'Data Management',
    question: 'Where is data stored geographically?',
    priority: 'Critical',
    responseType: 'List',
    scoringCriteria: 'Compliance with data residency requirements',
    weight: 9,
    template: 'All',
  },
  {
    id: 'tech-010',
    category: 'Technical Capabilities',
    subcategory: 'Data Management',
    question: 'What is your data retention policy?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Meets our compliance needs',
    weight: 6,
    template: 'All',
  },
  {
    id: 'tech-011',
    category: 'Technical Capabilities',
    subcategory: 'Architecture',
    question: 'What cloud provider(s) do you use?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'Tier 1 provider (AWS, Azure, GCP)',
    weight: 6,
    template: 'SaaS',
  },
  {
    id: 'tech-012',
    category: 'Technical Capabilities',
    subcategory: 'Architecture',
    question: 'Do you support multi-region deployment?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Important for global operations',
    weight: 5,
    template: 'SaaS',
  },
  {
    id: 'tech-013',
    category: 'Technical Capabilities',
    subcategory: 'Performance',
    question: 'What is your database technology and replication strategy?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Modern DB + multi-region replication',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'tech-014',
    category: 'Technical Capabilities',
    subcategory: 'Performance',
    question: 'How do you handle caching?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Multi-layer caching strategy',
    weight: 5,
    template: 'SaaS',
  },
  {
    id: 'tech-015',
    category: 'Technical Capabilities',
    subcategory: 'Integration',
    question: 'What is your API rate limiting policy?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Clear limits that meet our needs',
    weight: 6,
    template: 'SaaS',
  },
  {
    id: 'tech-016',
    category: 'Technical Capabilities',
    subcategory: 'Integration',
    question: 'Do you support SSO/SAML integration?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Required for enterprise',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'tech-017',
    category: 'Technical Capabilities',
    subcategory: 'Data Management',
    question: 'What backup frequency and retention period do you maintain?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Daily backups, 30+ day retention',
    weight: 8,
    template: 'All',
  },
  {
    id: 'tech-018',
    category: 'Technical Capabilities',
    subcategory: 'Data Management',
    question: 'What is your RTO (Recovery Time Objective)?',
    priority: 'Critical',
    responseType: 'Numeric',
    scoringCriteria: '<4 hours = 10, <24 hours = 7',
    weight: 9,
    template: 'All',
  },
  {
    id: 'tech-019',
    category: 'Technical Capabilities',
    subcategory: 'Data Management',
    question: 'What is your RPO (Recovery Point Objective)?',
    priority: 'Critical',
    responseType: 'Numeric',
    scoringCriteria: '<1 hour = 10, <24 hours = 7',
    weight: 9,
    template: 'All',
  },
  {
    id: 'tech-020',
    category: 'Technical Capabilities',
    subcategory: 'Monitoring',
    question: 'What monitoring and observability tools do you use?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'Modern APM + logging + metrics',
    weight: 6,
    template: 'SaaS',
  },

  // ==================== SECURITY & COMPLIANCE ====================
  {
    id: 'sec-001',
    category: 'Security & Compliance',
    subcategory: 'Certifications',
    question: 'What security certifications do you hold (SOC 2, ISO 27001, etc.)?',
    priority: 'Critical',
    responseType: 'List',
    scoringCriteria: 'SOC 2 Type II required',
    weight: 10,
    template: 'All',
  },
  {
    id: 'sec-002',
    category: 'Security & Compliance',
    subcategory: 'Certifications',
    question: 'Are you GDPR compliant?',
    priority: 'Critical',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes = 10, No = 0',
    weight: 10,
    template: 'All',
  },
  {
    id: 'sec-003',
    category: 'Security & Compliance',
    subcategory: 'Certifications',
    question: 'Are you HIPAA compliant (if applicable)?',
    priority: 'Critical',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes = 10, No = 0 (if healthcare)',
    weight: 10,
    template: 'Healthcare',
  },
  {
    id: 'sec-004',
    category: 'Security & Compliance',
    subcategory: 'Security Practices',
    question: 'Describe your encryption practices (at rest and in transit)',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'AES-256 at rest, TLS 1.3 in transit',
    weight: 9,
    template: 'All',
  },
  {
    id: 'sec-005',
    category: 'Security & Compliance',
    subcategory: 'Security Practices',
    question: 'How often do you conduct penetration testing?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Quarterly or more = 10, Annually = 7, Less = 3',
    weight: 7,
    template: 'All',
  },
  {
    id: 'sec-006',
    category: 'Security & Compliance',
    subcategory: 'Security Practices',
    question: 'Do you have a bug bounty program?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes = 5, No = 0 (bonus points)',
    weight: 3,
    template: 'SaaS',
  },
  {
    id: 'sec-007',
    category: 'Security & Compliance',
    subcategory: 'Incident Response',
    question: 'What is your incident response SLA?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: '<1 hour for critical = 10, <4 hours = 7',
    weight: 8,
    template: 'All',
  },
  {
    id: 'sec-008',
    category: 'Security & Compliance',
    subcategory: 'Incident Response',
    question: 'Have you had any security breaches in the past 3 years?',
    priority: 'Critical',
    responseType: 'Yes/No',
    scoringCriteria: 'No = 10, Yes (with good response) = 5',
    weight: 9,
    template: 'All',
  },
  {
    id: 'sec-009',
    category: 'Security & Compliance',
    subcategory: 'Access Control',
    question: 'What authentication methods do you support (SSO, SAML, MFA)?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'SSO + MFA required',
    weight: 7,
    template: 'All',
  },
  {
    id: 'sec-010',
    category: 'Security & Compliance',
    subcategory: 'Access Control',
    question: 'Do you support role-based access control (RBAC)?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes with granular controls = 10',
    weight: 6,
    template: 'All',
  },
  {
    id: 'sec-011',
    category: 'Security & Compliance',
    subcategory: 'Security Practices',
    question: 'What is your vulnerability patching SLA?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Critical: <24h, High: <7 days',
    weight: 8,
    template: 'All',
  },
  {
    id: 'sec-012',
    category: 'Security & Compliance',
    subcategory: 'Security Practices',
    question: 'Do you conduct employee security training?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Regular training required',
    weight: 6,
    template: 'All',
  },
  {
    id: 'sec-013',
    category: 'Security & Compliance',
    subcategory: 'Certifications',
    question: 'Are you PCI-DSS compliant (if applicable)?',
    priority: 'Critical',
    responseType: 'Yes/No',
    scoringCriteria: 'Required if processing payments',
    weight: 10,
    template: 'Payments',
  },
  {
    id: 'sec-014',
    category: 'Security & Compliance',
    subcategory: 'Access Control',
    question: 'What is your password policy?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Strong requirements + rotation',
    weight: 5,
    template: 'All',
  },
  {
    id: 'sec-015',
    category: 'Security & Compliance',
    subcategory: 'Access Control',
    question: 'Do you support IP whitelisting?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Important for corporate security',
    weight: 4,
    template: 'All',
  },
  {
    id: 'sec-016',
    category: 'Security & Compliance',
    subcategory: 'Incident Response',
    question: 'Do you have cyber insurance?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Coverage amount adequate',
    weight: 6,
    template: 'All',
  },
  {
    id: 'sec-017',
    category: 'Security & Compliance',
    subcategory: 'Security Practices',
    question: 'What DDoS protection do you have in place?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Cloudflare/AWS Shield or equivalent',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'sec-018',
    category: 'Security & Compliance',
    subcategory: 'Security Practices',
    question: 'Do you perform background checks on employees?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes for access to customer data',
    weight: 6,
    template: 'All',
  },
  {
    id: 'sec-019',
    category: 'Security & Compliance',
    subcategory: 'Data Management',
    question: 'What is your data deletion/purging process?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'GDPR-compliant deletion',
    weight: 7,
    template: 'All',
  },
  {
    id: 'sec-020',
    category: 'Security & Compliance',
    subcategory: 'Audit & Logging',
    question: 'What audit logging capabilities do you provide?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Complete audit trail with retention',
    weight: 7,
    template: 'All',
  },

  // ==================== SUPPORT & SLAs ====================
  {
    id: 'sup-001',
    category: 'Support & SLAs',
    subcategory: 'Support Availability',
    question: 'What are your support hours?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: '24/7 = 10, Business hours = 5',
    weight: 7,
    template: 'All',
  },
  {
    id: 'sup-002',
    category: 'Support & SLAs',
    subcategory: 'Support Availability',
    question: 'What support channels do you offer (email, phone, chat, ticketing)?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'Multiple channels including phone',
    weight: 6,
    template: 'All',
  },
  {
    id: 'sup-003',
    category: 'Support & SLAs',
    subcategory: 'Response Times',
    question: 'What is your first response SLA for critical issues?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: '<30 min = 10, <1 hour = 8, <4 hours = 5',
    weight: 9,
    template: 'All',
  },
  {
    id: 'sup-004',
    category: 'Support & SLAs',
    subcategory: 'Response Times',
    question: 'What is your resolution SLA for critical issues?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: '<4 hours = 10, <24 hours = 7',
    weight: 9,
    template: 'All',
  },
  {
    id: 'sup-005',
    category: 'Support & SLAs',
    subcategory: 'Account Management',
    question: 'Will we have a dedicated account manager?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes = 5, No = 0',
    weight: 4,
    template: 'All',
  },
  {
    id: 'sup-006',
    category: 'Support & SLAs',
    subcategory: 'Account Management',
    question: 'Will we have a dedicated technical account manager (TAM)?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes = 7, No = 0',
    weight: 6,
    template: 'All',
  },
  {
    id: 'sup-007',
    category: 'Support & SLAs',
    subcategory: 'Documentation',
    question: 'Do you provide comprehensive API documentation?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes with examples = 10',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'sup-008',
    category: 'Support & SLAs',
    subcategory: 'Documentation',
    question: 'Do you offer training and onboarding?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes = 5, No = 0',
    weight: 4,
    template: 'All',
  },
  {
    id: 'sup-009',
    category: 'Support & SLAs',
    subcategory: 'Support Availability',
    question: 'What support tiers/plans do you offer?',
    priority: 'Medium',
    responseType: 'List',
    scoringCriteria: 'Clear tiers with defined SLAs',
    weight: 5,
    template: 'All',
  },
  {
    id: 'sup-010',
    category: 'Support & SLAs',
    subcategory: 'Response Times',
    question: 'What is your average time to resolution for all issues?',
    priority: 'High',
    responseType: 'Numeric',
    scoringCriteria: '<24 hours = 10, <48 hours = 7',
    weight: 6,
    template: 'All',
  },
  {
    id: 'sup-011',
    category: 'Support & SLAs',
    subcategory: 'Documentation',
    question: 'Do you maintain a knowledge base or help center?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Comprehensive and searchable',
    weight: 4,
    template: 'All',
  },
  {
    id: 'sup-012',
    category: 'Support & SLAs',
    subcategory: 'Support Availability',
    question: 'Do you provide emergency escalation paths?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Clear escalation process',
    weight: 7,
    template: 'All',
  },

  // ==================== PRICING & CONTRACT ====================
  {
    id: 'price-001',
    category: 'Pricing & Contract',
    subcategory: 'Pricing Model',
    question: 'What is your pricing model (per user, usage-based, flat fee)?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Predictable + scalable',
    weight: 8,
    template: 'All',
  },
  {
    id: 'price-002',
    category: 'Pricing & Contract',
    subcategory: 'Pricing Model',
    question: 'What is included in the base price?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'All essential features included',
    weight: 7,
    template: 'All',
  },
  {
    id: 'price-003',
    category: 'Pricing & Contract',
    subcategory: 'Pricing Model',
    question: 'Are there any additional fees (implementation, support, training)?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'Transparency + reasonable fees',
    weight: 7,
    template: 'All',
  },
  {
    id: 'price-004',
    category: 'Pricing & Contract',
    subcategory: 'Contract Terms',
    question: 'What contract terms do you offer (monthly, annual, multi-year)?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'Flexible terms available',
    weight: 6,
    template: 'All',
  },
  {
    id: 'price-005',
    category: 'Pricing & Contract',
    subcategory: 'Contract Terms',
    question: 'What is your cancellation policy?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: '30-day notice acceptable',
    weight: 6,
    template: 'All',
  },
  {
    id: 'price-006',
    category: 'Pricing & Contract',
    subcategory: 'Data Portability',
    question: 'If we cancel, how do we export our data?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Standard formats + reasonable timeline',
    weight: 8,
    template: 'All',
  },
  {
    id: 'price-007',
    category: 'Pricing & Contract',
    subcategory: 'SLA Guarantees',
    question: 'What uptime guarantees do you provide?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: '99.9% or higher with credits',
    weight: 9,
    template: 'SaaS',
  },
  {
    id: 'price-008',
    category: 'Pricing & Contract',
    subcategory: 'SLA Guarantees',
    question: 'What are the financial penalties if SLAs are not met?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Service credits or refunds',
    weight: 7,
    template: 'All',
  },
  {
    id: 'price-009',
    category: 'Pricing & Contract',
    subcategory: 'Pricing Model',
    question: 'Do you offer volume discounts?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Tiered pricing for scale',
    weight: 4,
    template: 'All',
  },
  {
    id: 'price-010',
    category: 'Pricing & Contract',
    subcategory: 'Contract Terms',
    question: 'What is your price increase policy?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Annual cap + advance notice',
    weight: 6,
    template: 'All',
  },
  {
    id: 'price-011',
    category: 'Pricing & Contract',
    subcategory: 'Contract Terms',
    question: 'Do you offer a free trial or pilot program?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'POC opportunity',
    weight: 5,
    template: 'All',
  },
  {
    id: 'price-012',
    category: 'Pricing & Contract',
    subcategory: 'Pricing Model',
    question: 'Are there overage charges? If so, what are the rates?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Clear and reasonable overage costs',
    weight: 6,
    template: 'All',
  },

  // ==================== IMPLEMENTATION & MIGRATION ====================
  {
    id: 'impl-001',
    category: 'Implementation & Migration',
    subcategory: 'Onboarding',
    question: 'What is the typical implementation timeline?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Realistic + meets our deadline',
    weight: 7,
    template: 'All',
  },
  {
    id: 'impl-002',
    category: 'Implementation & Migration',
    subcategory: 'Onboarding',
    question: 'Do you provide implementation support?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes with dedicated team = 10',
    weight: 7,
    template: 'All',
  },
  {
    id: 'impl-003',
    category: 'Implementation & Migration',
    subcategory: 'Migration',
    question: 'Do you offer data migration services from our current system?',
    priority: 'Critical',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes with migration plan = 10',
    weight: 8,
    template: 'All',
  },
  {
    id: 'impl-004',
    category: 'Implementation & Migration',
    subcategory: 'Migration',
    question: 'What is your approach to minimizing downtime during migration?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Zero-downtime or <1 hour',
    weight: 9,
    template: 'All',
  },
  {
    id: 'impl-005',
    category: 'Implementation & Migration',
    subcategory: 'Testing',
    question: 'Do you provide a staging/sandbox environment?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes = 10, No = 0',
    weight: 7,
    template: 'All',
  },
  {
    id: 'impl-006',
    category: 'Implementation & Migration',
    subcategory: 'Customization',
    question: 'What customization options are available?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Matches our requirements',
    weight: 5,
    template: 'All',
  },
  {
    id: 'impl-007',
    category: 'Implementation & Migration',
    subcategory: 'Onboarding',
    question: 'What is your implementation methodology (Agile, Waterfall, etc.)?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Structured approach',
    weight: 4,
    template: 'All',
  },
  {
    id: 'impl-008',
    category: 'Implementation & Migration',
    subcategory: 'Testing',
    question: 'What testing is included in the implementation?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'UAT + integration testing',
    weight: 6,
    template: 'All',
  },
  {
    id: 'impl-009',
    category: 'Implementation & Migration',
    subcategory: 'Migration',
    question: 'Can you provide a detailed migration plan?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Documented migration process',
    weight: 7,
    template: 'All',
  },
  {
    id: 'impl-010',
    category: 'Implementation & Migration',
    subcategory: 'Onboarding',
    question: 'What training do you provide to our team?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Comprehensive training plan',
    weight: 5,
    template: 'All',
  },

  // ==================== PRODUCT ROADMAP ====================
  {
    id: 'road-001',
    category: 'Product Roadmap',
    subcategory: 'Development',
    question: 'How often do you release new features?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Regular releases (monthly/quarterly)',
    weight: 4,
    template: 'SaaS',
  },
  {
    id: 'road-002',
    category: 'Product Roadmap',
    subcategory: 'Development',
    question: 'What features are on your roadmap for the next 12 months?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'Aligns with our needs',
    weight: 6,
    template: 'All',
  },
  {
    id: 'road-003',
    category: 'Product Roadmap',
    subcategory: 'Customer Input',
    question: 'How do you incorporate customer feedback into your roadmap?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Formal process with voting/input',
    weight: 4,
    template: 'All',
  },
  {
    id: 'road-004',
    category: 'Product Roadmap',
    subcategory: 'Deprecation',
    question: 'What is your policy for deprecating features?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: '6+ month notice with migration path',
    weight: 6,
    template: 'All',
  },
  {
    id: 'road-005',
    category: 'Product Roadmap',
    subcategory: 'Development',
    question: 'What is your R&D budget as a percentage of revenue?',
    priority: 'Medium',
    responseType: 'Numeric',
    scoringCriteria: '>15% indicates innovation focus',
    weight: 4,
    template: 'SaaS',
  },
  {
    id: 'road-006',
    category: 'Product Roadmap',
    subcategory: 'Innovation',
    question: 'Are you investing in AI/ML capabilities?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Future-proofing',
    weight: 5,
    template: 'SaaS',
  },
  {
    id: 'road-007',
    category: 'Product Roadmap',
    subcategory: 'Customer Input',
    question: 'Do you have a customer advisory board?',
    priority: 'Low',
    responseType: 'Yes/No',
    scoringCriteria: 'Customer influence on direction',
    weight: 3,
    template: 'All',
  },

  // ==================== REFERENCES ====================
  {
    id: 'ref-001',
    category: 'References',
    subcategory: 'Customer References',
    question: 'Can you provide 3 customer references in our industry?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes with similar scale = 10',
    weight: 7,
    template: 'All',
  },
  {
    id: 'ref-002',
    category: 'References',
    subcategory: 'Case Studies',
    question: 'Can you provide case studies of similar implementations?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes with measurable results = 10',
    weight: 7,
    template: 'All',
  },
  {
    id: 'ref-003',
    category: 'References',
    subcategory: 'Customer Success',
    question: 'What is your largest customer (by users/volume)?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Exceeds our scale',
    weight: 5,
    template: 'All',
  },
  {
    id: 'ref-004',
    category: 'References',
    subcategory: 'Customer Success',
    question: 'What is your Net Promoter Score (NPS)?',
    priority: 'Medium',
    responseType: 'Numeric',
    scoringCriteria: '>50 = excellent, >30 = good',
    weight: 5,
    template: 'All',
  },
  {
    id: 'ref-005',
    category: 'References',
    subcategory: 'Customer References',
    question: 'Can we speak with your implementation team from a recent project?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Direct feedback valuable',
    weight: 6,
    template: 'All',
  },

  // ==================== DISASTER RECOVERY & BUSINESS CONTINUITY ====================
  {
    id: 'dr-001',
    category: 'Disaster Recovery & Business Continuity',
    subcategory: 'DR Planning',
    question: 'Do you have a documented disaster recovery plan?',
    priority: 'Critical',
    responseType: 'Yes/No',
    scoringCriteria: 'Tested DR plan required',
    weight: 9,
    template: 'All',
  },
  {
    id: 'dr-002',
    category: 'Disaster Recovery & Business Continuity',
    subcategory: 'DR Planning',
    question: 'How often do you test your disaster recovery plan?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Quarterly = 10, Annually = 7',
    weight: 8,
    template: 'All',
  },
  {
    id: 'dr-003',
    category: 'Disaster Recovery & Business Continuity',
    subcategory: 'Redundancy',
    question: 'What redundancy do you have in your infrastructure?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Multi-region, multi-AZ',
    weight: 9,
    template: 'SaaS',
  },
  {
    id: 'dr-004',
    category: 'Disaster Recovery & Business Continuity',
    subcategory: 'Business Continuity',
    question: 'Do you have a business continuity plan?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'BCP with testing',
    weight: 7,
    template: 'All',
  },
  {
    id: 'dr-005',
    category: 'Disaster Recovery & Business Continuity',
    subcategory: 'Redundancy',
    question: 'What is your failover strategy?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Automatic failover',
    weight: 9,
    template: 'SaaS',
  },
  {
    id: 'dr-006',
    category: 'Disaster Recovery & Business Continuity',
    subcategory: 'DR Planning',
    question: 'What is your data backup strategy and frequency?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Continuous or hourly backups',
    weight: 9,
    template: 'All',
  },
  {
    id: 'dr-007',
    category: 'Disaster Recovery & Business Continuity',
    subcategory: 'Incident Management',
    question: 'Describe your incident management process',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'ITIL-based or equivalent',
    weight: 7,
    template: 'All',
  },
  {
    id: 'dr-008',
    category: 'Disaster Recovery & Business Continuity',
    subcategory: 'Communication',
    question: 'How do you communicate during outages?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Status page + email + multiple channels',
    weight: 7,
    template: 'All',
  },

  // ==================== PERFORMANCE & SCALABILITY ====================
  {
    id: 'perf-001',
    category: 'Performance & Scalability',
    subcategory: 'Load Testing',
    question: 'What load testing do you perform?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Regular stress testing',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'perf-002',
    category: 'Performance & Scalability',
    subcategory: 'Capacity Planning',
    question: 'How do you handle capacity planning?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Proactive monitoring + scaling',
    weight: 6,
    template: 'SaaS',
  },
  {
    id: 'perf-003',
    category: 'Performance & Scalability',
    subcategory: 'Performance Monitoring',
    question: 'What performance metrics do you monitor?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'Response time, throughput, errors',
    weight: 6,
    template: 'SaaS',
  },
  {
    id: 'perf-004',
    category: 'Performance & Scalability',
    subcategory: 'Scalability',
    question: 'What is the maximum number of concurrent users you support?',
    priority: 'High',
    responseType: 'Numeric',
    scoringCriteria: 'Exceeds our needs by 5x',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'perf-005',
    category: 'Performance & Scalability',
    subcategory: 'Performance SLAs',
    question: 'Do you provide performance SLAs?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Response time guarantees',
    weight: 5,
    template: 'SaaS',
  },
  {
    id: 'perf-006',
    category: 'Performance & Scalability',
    subcategory: 'CDN',
    question: 'Do you use a CDN for static assets?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Global performance',
    weight: 4,
    template: 'SaaS',
  },

  // ==================== VENDOR MANAGEMENT ====================
  {
    id: 'vendor-001',
    category: 'Vendor Management',
    subcategory: 'Communication',
    question: 'How often are account reviews conducted?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Quarterly reviews',
    weight: 4,
    template: 'All',
  },
  {
    id: 'vendor-002',
    category: 'Vendor Management',
    subcategory: 'Transparency',
    question: 'Do you provide a public status page?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Real-time status updates',
    weight: 6,
    template: 'SaaS',
  },
  {
    id: 'vendor-003',
    category: 'Vendor Management',
    subcategory: 'Transparency',
    question: 'Do you publish post-mortems for major incidents?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Transparency and learning',
    weight: 5,
    template: 'SaaS',
  },
  {
    id: 'vendor-004',
    category: 'Vendor Management',
    subcategory: 'Partnership',
    question: 'Do you have a partner ecosystem?',
    priority: 'Low',
    responseType: 'Yes/No',
    scoringCriteria: 'Integration opportunities',
    weight: 3,
    template: 'All',
  },
  {
    id: 'vendor-005',
    category: 'Vendor Management',
    subcategory: 'Communication',
    question: 'How do you handle change notifications?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: '30-day advance notice for breaking changes',
    weight: 6,
    template: 'All',
  },
  {
    id: 'vendor-006',
    category: 'Vendor Management',
    subcategory: 'Exit Strategy',
    question: 'What is the process if we decide to terminate?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Clear offboarding process',
    weight: 6,
    template: 'All',
  },

  // ==================== AI & EMERGING TECHNOLOGY ====================
  {
    id: 'ai-001',
    category: 'AI & Emerging Technology',
    subcategory: 'AI Usage',
    question: 'Do you use AI/ML in your product or services?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Clear disclosure of AI usage',
    weight: 7,
    template: 'All',
  },
  {
    id: 'ai-002',
    category: 'AI & Emerging Technology',
    subcategory: 'AI Usage',
    question: 'What specific AI features or capabilities do you offer?',
    priority: 'High',
    responseType: 'List',
    scoringCriteria: 'Relevant to our needs',
    weight: 6,
    template: 'SaaS',
  },
  {
    id: 'ai-003',
    category: 'AI & Emerging Technology',
    subcategory: 'AI Transparency',
    question: 'What AI models do you use (e.g., GPT-4, Claude, proprietary)?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Full transparency on models used',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'ai-004',
    category: 'AI & Emerging Technology',
    subcategory: 'AI Transparency',
    question: 'How do you ensure AI-generated outputs are accurate and reliable?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Validation process + human oversight',
    weight: 9,
    template: 'SaaS',
  },
  {
    id: 'ai-005',
    category: 'AI & Emerging Technology',
    subcategory: 'Data Privacy',
    question: 'Is our data used to train your AI models?',
    priority: 'Critical',
    responseType: 'Yes/No',
    scoringCriteria: 'No = 10, Yes with opt-out = 5',
    weight: 10,
    template: 'All',
  },
  {
    id: 'ai-006',
    category: 'AI & Emerging Technology',
    subcategory: 'Data Privacy',
    question: 'How do you prevent data leakage between customers in AI systems?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Strong isolation guarantees',
    weight: 10,
    template: 'SaaS',
  },
  {
    id: 'ai-007',
    category: 'AI & Emerging Technology',
    subcategory: 'Data Privacy',
    question: 'Do you support private/on-premise AI model deployment?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes for sensitive data = 10',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'ai-008',
    category: 'AI & Emerging Technology',
    subcategory: 'Ethics & Bias',
    question: 'How do you test for and mitigate AI bias?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Regular bias testing + mitigation',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'ai-009',
    category: 'AI & Emerging Technology',
    subcategory: 'Ethics & Bias',
    question: 'Do you have an AI ethics policy or framework?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Published policy = 10',
    weight: 5,
    template: 'All',
  },
  {
    id: 'ai-010',
    category: 'AI & Emerging Technology',
    subcategory: 'Ethics & Bias',
    question: 'What measures prevent harmful or inappropriate AI outputs?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Content filtering + monitoring',
    weight: 8,
    template: 'SaaS',
  },
  {
    id: 'ai-011',
    category: 'AI & Emerging Technology',
    subcategory: 'Governance',
    question: 'Can we audit your AI decision-making processes?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Yes with explainability = 10',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'ai-012',
    category: 'AI & Emerging Technology',
    subcategory: 'Governance',
    question: 'Do you provide AI usage analytics and monitoring?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Detailed usage dashboards',
    weight: 5,
    template: 'SaaS',
  },
  {
    id: 'ai-013',
    category: 'AI & Emerging Technology',
    subcategory: 'Integration',
    question: 'Can we integrate our own AI models with your platform?',
    priority: 'Medium',
    responseType: 'Yes/No',
    scoringCriteria: 'Flexible integration options',
    weight: 5,
    template: 'SaaS',
  },
  {
    id: 'ai-014',
    category: 'AI & Emerging Technology',
    subcategory: 'Integration',
    question: 'What AI model providers do you support (OpenAI, Anthropic, Google, etc.)?',
    priority: 'Medium',
    responseType: 'List',
    scoringCriteria: 'Multiple provider support',
    weight: 4,
    template: 'SaaS',
  },
  {
    id: 'ai-015',
    category: 'AI & Emerging Technology',
    subcategory: 'Compliance',
    question: 'Are you compliant with AI regulations (EU AI Act, etc.)?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Current compliance + roadmap',
    weight: 8,
    template: 'All',
  },
  {
    id: 'ai-016',
    category: 'AI & Emerging Technology',
    subcategory: 'Compliance',
    question: 'How do you handle AI-generated content attribution and copyright?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Clear legal framework',
    weight: 7,
    template: 'SaaS',
  },
  {
    id: 'ai-017',
    category: 'AI & Emerging Technology',
    subcategory: 'Performance',
    question: 'What is the typical latency for AI-powered features?',
    priority: 'Medium',
    responseType: 'Text',
    scoringCriteria: 'Sub-second response times',
    weight: 5,
    template: 'SaaS',
  },
  {
    id: 'ai-018',
    category: 'AI & Emerging Technology',
    subcategory: 'Performance',
    question: 'How do you handle AI service degradation or outages?',
    priority: 'High',
    responseType: 'Text',
    scoringCriteria: 'Graceful fallbacks + notifications',
    weight: 6,
    template: 'SaaS',
  },
  {
    id: 'ai-019',
    category: 'AI & Emerging Technology',
    subcategory: 'Cost',
    question: 'How is AI usage priced (per request, per token, flat fee)?',
    priority: 'Critical',
    responseType: 'Text',
    scoringCriteria: 'Transparent and predictable pricing',
    weight: 8,
    template: 'SaaS',
  },
  {
    id: 'ai-020',
    category: 'AI & Emerging Technology',
    subcategory: 'Cost',
    question: 'Are there usage caps or rate limits on AI features?',
    priority: 'High',
    responseType: 'Yes/No',
    scoringCriteria: 'Clear limits documented',
    weight: 6,
    template: 'SaaS',
  },
];

export const QUESTION_TEMPLATES = [
  'All',
  'SaaS',
  'Consulting',
  'Infrastructure',
  'Security',
  'Healthcare',
  'Payments',
] as const;

export type QuestionTemplate = typeof QUESTION_TEMPLATES[number];
